<template>
  <div class="alimtalk-card">
    <a-steps :current="currentStep" class="migration-steps">
      <a-step title="준비" description="채널 선택 및 연결 파일 업로드" />
      <a-step title="파싱" description="브라우저에서 데이터 추출" />
      <a-step title="드라이런" description="등록 전 검증" />
      <a-step title="등록 실행" description="CRM 등록 및 검수" />
    </a-steps>

    <!-- ============ Step 1. 준비 ============ -->
    <div v-if="currentStep === 0" class="step-body">
      <!-- 병원은 상단 공용 선택기에서 고른다. 아직 안 골랐으면 여기서 막는다. -->
      <a-alert
        v-if="!companyId"
        type="warning"
        show-icon
        class="need-company"
        message="대상 병원을 먼저 선택하세요"
        description="상단의 대상 병원 선택기에서 병원을 고르면 해당 병원의 알림톡 채널을 불러옵니다."
      />

      <!-- 병원이 채널을 여러 개 가질 수 있어 발신프로필을 반드시 지정해야 한다 -->
      <div v-if="companyId" class="field-block">
        <label class="field-label">알림톡 채널 <span class="required-mark">*</span></label>

        <a-spin :spinning="channelLoading">
          <a-alert
            v-if="!channelLoading && !channelList.length"
            type="error"
            show-icon
            message="연결된 알림톡 채널이 없습니다"
            description="이 병원에는 등록된 카카오 알림톡 채널이 없어 템플릿을 등록할 수 없습니다. 인증정보 관리에서 채널을 먼저 등록해주세요."
          />

          <template v-else>
            <!-- 채널이 2개 이상이면 운영자가 직접 선택해야 한다 -->
            <a-select
              v-if="channelList.length > 1"
              v-model:value="selectedChannelId"
              placeholder="등록할 채널을 선택하세요"
              style="width: 520px"
            >
              <a-select-option
                v-for="channel in channelList"
                :key="channel.channelId"
                :value="channel.channelId"
                :title="`${channel.channelName} (${channel.channelId}) / ${channel.senderKey}`"
              >
                {{ channel.channelName }} ({{ channel.channelId }})
                <a-tag v-if="channel.isPrimary" color="blue">대표</a-tag>
                <a-tag v-if="isRecommendedChannel(channel)" color="green">권장</a-tag>
                <span class="muted sender-key-inline">{{ truncateSenderKey(channel.senderKey) }}</span>
              </a-select-option>
            </a-select>

            <!-- 채널이 하나뿐이어도 어떤 발신프로필로 등록되는지 반드시 노출한다 -->
            <div v-if="selectedChannel" class="channel-summary">
              <div class="channel-summary-row">
                <strong>{{ selectedChannel.channelName }}</strong>
                <span class="muted">({{ selectedChannel.channelId }})</span>
                <a-tag v-if="selectedChannel.isPrimary" color="blue">대표</a-tag>
                <a-tag v-if="isRecommendedChannel(selectedChannel)" color="green">권장</a-tag>
                <a-tag v-if="selectedChannel.isActive === false" color="red">비활성</a-tag>
              </div>
              <div class="channel-summary-row">
                <span class="muted">발신프로필</span>
                <span class="sender-key">{{ selectedChannel.senderKey }}</span>
              </div>
            </div>

            <p v-if="!parsed" class="field-hint">
              연결 파일을 업로드하면 AfterDoc 이 실제로 사용하던 발신프로필을 <strong>권장</strong> 으로 표시합니다.
            </p>

            <a-alert
              v-if="senderKeyMismatch"
              type="warning"
              show-icon
              class="channel-warning"
              message="AfterDoc 이 사용하던 발신프로필과 다릅니다"
              :description="`템플릿 ${parsed.stats.templates}건이 모두 신규 등록되어 카카오 검수를 다시 받아야 합니다. AfterDoc 발신프로필: ${afterdocSenderKeys.join(', ')}`"
            />
          </template>
        </a-spin>
      </div>

      <!-- automationId 가 아직 없어 store 만으로 연결이 안 풀릴 때만 이 파일을 요구한다 -->
      <div v-if="linkage.needsApiBodies" class="field-block">
        <label class="field-label">collect-api-bodies.json <span class="required-mark">*</span></label>
        <a-upload-dragger
          :before-upload="handleApiBodiesSelect"
          :show-upload-list="false"
          accept=".json"
        >
          <p class="ant-upload-drag-icon"><InboxOutlined /></p>
          <p class="ant-upload-text">클릭하거나 파일을 드래그하여 업로드</p>
          <p class="ant-upload-hint">
            자동화 메시지에 automationId 가 아직 없어, 자동화-메시지 연결이 이 파일에만 있습니다.
            채팅 bodies(수 GB)가 아니라 자동화 연결 요청 몇 건만 담긴 파일이면 충분합니다.
          </p>
        </a-upload-dragger>
        <div v-if="apiBodiesFile" class="selected-file">
          <FileTextOutlined />
          <span class="file-name">{{ apiBodiesFile.name }}</span>
          <a-button type="link" danger size="small" @click="apiBodiesFile = null">삭제</a-button>
        </div>
      </div>

      <!-- automationId 로 연결이 풀리면 추가 파일이 필요 없다 -->
      <a-alert
        v-else-if="linkage.hasAutomations"
        type="success"
        show-icon
        class="channel-warning"
        message="자동화 연결 정보가 data-store.json 에 있어 추가 파일이 필요 없습니다"
        :description="`automationId 로 자동화 ${linkage.automationCount}개 · 메시지 ${linkage.messageCount}건 연결이 복원됩니다.`"
      />

      <div class="step-footer">
        <a-button
          type="primary"
          class="yellow-button"
          :loading="parsing"
          :disabled="!canGoNext"
          @click="currentStep = 1"
        >
          다음
        </a-button>
      </div>
    </div>

    <!-- ============ Step 2. 파싱 ============ -->
    <div v-if="currentStep === 1 && parsed" class="step-body">
      <div class="stats-cards">
        <a-row :gutter="16">
          <a-col :span="6">
            <a-card><a-statistic title="알림톡 템플릿" :value="parsed.stats.templates" suffix="건" /></a-card>
          </a-col>
          <a-col :span="6">
            <a-card><a-statistic title="시나리오" :value="parsed.stats.scenarios" suffix="개" /></a-card>
          </a-col>
          <a-col :span="6">
            <a-card><a-statistic title="매칭 태그명" :value="parsed.stats.tags" suffix="개" /></a-card>
          </a-col>
          <a-col :span="6">
            <a-card>
              <a-statistic
                title="활성 자동화"
                :value="parsed.stats.activeScenarios"
                :value-style="{ color: '#52c41a' }"
                suffix="개"
              />
            </a-card>
          </a-col>
        </a-row>
      </div>

      <a-tabs>
        <a-tab-pane key="templates" :tab="`알림톡 템플릿 (${parsed.stats.templates})`">
          <!-- AfterDoc 템플릿은 모두 텍스트형(BA · NONE)이다. 카카오 타입과 검수 상태를 그대로 노출한다. -->
          <a-alert
            type="info"
            show-icon
            class="type-notice"
            message="AfterDoc 템플릿은 모두 텍스트형(BA · NONE)입니다"
            description="주의사항 템플릿은 AfterDoc 발신프로필에 이미 승인(APR)되어 있어 같은 발신프로필을 고르면 재등록 없이 채택됩니다. 자동화 메시지는 신규 등록되어 카카오 검수를 새로 받습니다."
          />
          <a-table
            :columns="templateColumns"
            :data-source="templateRows"
            :pagination="{ pageSize: 20, showSizeChanger: false }"
            :row-key="(record) => `${record.kind}-${record.sourceId}`"
            size="small"
            :scroll="{ x: 1180 }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'kind'">
                <a-tag :color="TEMPLATE_KIND_COLOR_MAP[record.kind]">
                  {{ TEMPLATE_KIND_TEXT_MAP[record.kind] }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'type'">
                <a-tag color="geekblue" class="type-chip">
                  {{ record.messageType }} · {{ record.emphasizeType }} ({{ record.emphasizeLabel }})
                </a-tag>
              </template>
              <template v-else-if="column.key === 'inspection'">
                <a-tag v-if="record.adopted" color="green">승인됨 · 채택</a-tag>
                <a-tag v-else color="orange">신규 등록</a-tag>
              </template>
              <template v-else-if="column.key === 'preview'">
                <span class="content-preview">{{ record.preview }}</span>
              </template>
              <template v-else-if="column.key === 'variables'">
                <span v-if="!record.variables.length" class="muted">-</span>
                <a-tag v-for="variable in record.variables" :key="variable" color="geekblue">
                  {{ variable }}
                </a-tag>
              </template>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="scenarios" :tab="`시나리오 (${parsed.stats.scenarios})`">
          <a-table
            :columns="scenarioColumns"
            :data-source="scenarioRows"
            :pagination="{ pageSize: 20, showSizeChanger: false }"
            :row-key="(record) => record.sourceId"
            size="small"
            :scroll="{ x: 1100 }"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'isActive'">
                <a-tag :color="record.isActive ? 'green' : 'default'">
                  {{ record.isActive ? "활성" : "비활성" }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'tagNames'">
                <span v-if="!record.tagNames.length" class="muted">-</span>
                <a-tag v-for="tagName in record.tagNames" :key="tagName">{{ tagName }}</a-tag>
              </template>
              <template v-else-if="column.key === 'timeline'">
                <span class="timeline">{{ record.timeline || "-" }}</span>
              </template>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="tags" :tab="`매칭 태그명 (${parsed.stats.tags})`">
          <!-- crm 태그를 만들지 않는다. 시나리오가 이 "이름" 으로 고객 태그를 텍스트 매칭한다.
               상담원이 Studio 에서 붙이는 이름과 한 글자라도 다르면 조용히 안 걸린다. -->
          <a-alert
            type="info"
            show-icon
            class="tag-notice"
            message="crm 태그는 만들지 않습니다"
            description="시나리오는 고객 태그(traits.consultTags)를 아래 이름과 텍스트로 대조해 발동합니다. 상담원이 Studio 에서 붙이는 태그명이 아래와 정확히 같아야 하며, 한 글자라도 다르면 시나리오가 걸리지 않습니다."
          />
          <a-table
            :columns="tagColumns"
            :data-source="parsed.tags"
            :pagination="{ pageSize: 20, showSizeChanger: false }"
            :row-key="(record) => record.sourceId"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'color'">
                <span class="color-cell">
                  <span class="color-swatch" :style="{ backgroundColor: record.color || '#d9d9d9' }"></span>
                  <span class="muted">{{ record.color || "-" }}</span>
                </span>
              </template>
            </template>
          </a-table>
        </a-tab-pane>
      </a-tabs>

      <div class="step-footer">
        <a-button @click="currentStep = 0">이전</a-button>
        <a-button type="primary" class="yellow-button" :loading="previewing" @click="handlePreview">
          드라이런 실행
        </a-button>
      </div>
    </div>

    <!-- ============ Step 3. 드라이런 ============ -->
    <div v-if="currentStep === 2" class="step-body">
      <a-alert
        v-if="previewResult && !previewResult.channel"
        type="error"
        show-icon
        message="알림톡 채널을 사용할 수 없습니다"
        :description="channelErrorReason || '선택한 병원에 등록된 카카오 알림톡 채널이 없어 템플릿을 등록할 수 없습니다. 인증정보 관리에서 채널을 먼저 등록하세요.'"
        class="block-alert"
      />

      <template v-if="previewResult">
        <div class="summary-strip">
          <a-descriptions bordered size="small" :column="3">
            <a-descriptions-item label="채널">
              <template v-if="previewResult.channel">
                {{ previewResult.channel.channelName }} ({{ previewResult.channel.channelId }})
              </template>
              <span v-else class="muted">없음</span>
            </a-descriptions-item>
            <a-descriptions-item label="매칭 태그명">
              {{ previewResult.summary.tags.match }}개 (crm 태그는 만들지 않음)
            </a-descriptions-item>
            <a-descriptions-item label="템플릿">
              신규(등록·검수) {{ previewResult.summary.templates.create }} / 채택
              {{ previewResult.summary.templates.adopt }} / 병합(코드공유)
              {{ previewResult.summary.templates.merge }} / 스킵
              {{ previewResult.summary.templates.skip }} / 오류 {{ previewResult.summary.templates.error }}
            </a-descriptions-item>
            <a-descriptions-item label="시나리오">
              신규 {{ previewResult.summary.scenarios.create }} / 스킵
              {{ previewResult.summary.scenarios.skip }} / 오류 {{ previewResult.summary.scenarios.error }}
            </a-descriptions-item>
            <a-descriptions-item label="검증 결과" :span="2">
              <a-tag :color="previewResult.ok ? 'green' : 'red'">
                {{ previewResult.ok ? "등록 가능" : "등록 불가" }}
              </a-tag>
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <!-- ===== 시나리오 구성 (실제로 만들어질 플로우) ===== -->
        <div v-if="blueprints.length" class="blueprint-section">
          <div class="section-header">
            <h5>시나리오 구성</h5>
            <span class="muted">
              시나리오 {{ blueprints.length }}개 중
              <strong v-if="blockedBlueprintCount" class="error-text">{{ blockedBlueprintCount }}개</strong>
              <strong v-else>0개</strong>
              가 지금 상태로는 발동할 수 없습니다 (draft 로 생성됨)
            </span>
          </div>

          <div class="blueprint-layout">
            <div class="blueprint-picker">
              <div
                v-for="blueprint in blueprints"
                :key="blueprint.sourceId"
                class="picker-item"
                :class="{ active: blueprint.sourceId === selectedBlueprintId }"
                @click="selectedBlueprintId = blueprint.sourceId"
              >
                <span class="picker-dot" :class="{ blocked: blueprint.blockers.length }"></span>
                <span class="picker-name">{{ blueprint.name }}</span>
                <span class="muted picker-count">{{ blueprint.steps.length }}단계</span>
              </div>
            </div>

            <div class="blueprint-detail">
              <ScenarioBlueprint v-if="selectedBlueprint" :blueprint="selectedBlueprint" />
            </div>
          </div>
        </div>

        <div class="filter-row">
          <a-checkbox v-model:checked="showProblemOnly">경고 / 오류만 보기</a-checkbox>
          <span class="muted">전체 {{ previewResult.items.length }}건 / 표시 {{ previewItems.length }}건</span>
        </div>

        <a-table
          :columns="previewColumns"
          :data-source="previewItems"
          :pagination="{ pageSize: 20, showSizeChanger: false }"
          :row-key="(record) => `${record.kind}-${record.sourceId}`"
          size="small"
          :scroll="{ x: 900 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'kind'">
              {{ ITEM_KIND_TEXT_MAP[record.kind] || record.kind }}
            </template>
            <template v-else-if="column.key === 'verdict'">
              <a-tag :color="VERDICT_COLOR_MAP[record.verdict]">
                {{ VERDICT_TEXT_MAP[record.verdict] || record.verdict }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'reasons'">
              <span v-if="!record.reasons || !record.reasons.length" class="muted">-</span>
              <ul v-else class="reason-list">
                <li v-for="(reason, index) in record.reasons" :key="index">{{ reason }}</li>
              </ul>
            </template>
          </template>
        </a-table>
      </template>

      <div class="step-footer">
        <a-button @click="currentStep = 1">이전</a-button>
        <a-button :loading="previewing" @click="handlePreview">다시 검증</a-button>
        <a-button
          type="primary"
          class="yellow-button"
          :disabled="!previewResult || !previewResult.ok"
          @click="confirmVisible = true"
        >
          등록 실행
        </a-button>
      </div>
    </div>

    <!-- ============ Step 4. 등록 실행 ============ -->
    <div v-if="currentStep === 3" class="step-body">
      <template v-if="applyResult">
        <div class="summary-strip">
          <a-descriptions bordered size="small" :column="3">
            <a-descriptions-item label="매칭 태그명">
              {{ applyResult.summary.tags.match }}개 (crm 태그는 만들지 않음)
            </a-descriptions-item>
            <a-descriptions-item label="템플릿">
              신규(등록·검수) {{ applyResult.summary.templates.create }} / 채택
              {{ applyResult.summary.templates.adopt }} / 병합(코드공유)
              {{ applyResult.summary.templates.merge }} / 스킵
              {{ applyResult.summary.templates.skip }} / 오류 {{ applyResult.summary.templates.error }}
            </a-descriptions-item>
            <a-descriptions-item label="시나리오">
              신규 {{ applyResult.summary.scenarios.create }} / 스킵
              {{ applyResult.summary.scenarios.skip }} / 오류 {{ applyResult.summary.scenarios.error }}
            </a-descriptions-item>
          </a-descriptions>
        </div>

        <div class="filter-row">
          <span class="muted">전체 {{ applyResult.items.length }}건 / 실패 {{ failedItems.length }}건</span>
          <a-button
            v-if="failedItems.length"
            type="primary"
            class="yellow-button"
            size="small"
            :loading="applying"
            @click="handleRetryFailed"
          >
            <template #icon><RedoOutlined /></template>
            실패건만 다시 실행
          </a-button>
        </div>

        <a-table
          :columns="applyColumns"
          :data-source="applyResult.items"
          :pagination="{ pageSize: 20, showSizeChanger: false }"
          :row-key="(record) => `${record.kind}-${record.sourceId}`"
          size="small"
          :scroll="{ x: 1000 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'kind'">
              {{ ITEM_KIND_TEXT_MAP[record.kind] || record.kind }}
            </template>
            <template v-else-if="column.key === 'result'">
              <a-tag :color="RESULT_COLOR_MAP[record.result]">
                {{ RESULT_TEXT_MAP[record.result] || record.result }}
              </a-tag>
              <!-- 이미 DKT 에 있던 템플릿을 재등록 없이 가져온 경우 -->
              <a-tag v-if="record.adopted" color="cyan">기존 채택</a-tag>
            </template>
            <template v-else-if="column.key === 'error'">
              <span v-if="!record.error" class="muted">-</span>
              <span v-else class="error-text">{{ record.error }}</span>
            </template>
          </template>
        </a-table>
      </template>

      <!-- 검수 상태 패널 -->
      <div class="inspection-panel">
        <div class="panel-header">
          <h5>검수 상태</h5>
          <a-button size="small" :loading="inspectionLoading" @click="handleRefreshInspection">
            <template #icon><ReloadOutlined /></template>
            새로고침
          </a-button>
        </div>

        <!-- 새로고침은 단순 조회가 아니다. 승인 상태를 템플릿에 반영해야 시나리오를 켤 수 있다. -->
        <a-alert
          type="info"
          show-icon
          class="inspection-note"
          message="승인(O)이 되어야 시나리오를 활성화할 수 있습니다"
          description="새로고침하면 카카오 검수 결과가 템플릿에 반영됩니다. 미검수(N)·검수중(I)·반려(R) 상태에서는 시나리오 활성화가 거부됩니다. 승인 후 Studio 에서 직접 켜주세요."
        />
        <a-table
          :columns="inspectionColumns"
          :data-source="inspectionItems"
          :loading="inspectionLoading"
          :pagination="{ pageSize: 10, showSizeChanger: false }"
          :row-key="(record) => record.templateCode"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'status'">
              <a-tag :color="INSPECTION_STATUS_COLOR_MAP[record.status]">
                {{ INSPECTION_STATUS_TEXT_MAP[record.status] || record.status }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'comment'">
              <span v-if="!record.comment" class="muted">-</span>
              <span v-else>{{ record.comment }}</span>
            </template>
          </template>
        </a-table>
      </div>

      <div class="step-footer">
        <a-button @click="currentStep = 2">이전</a-button>
      </div>
    </div>

    <!-- 등록 실행 확인 모달 -->
    <a-modal :open="confirmVisible" :footer="null" @cancel="confirmVisible = false">
      <template #title>
        <div class="modal-title">등록 실행 확인</div>
      </template>
      <div class="modal-content">
        <a-alert
          type="warning"
          show-icon
          message="카카오에 템플릿이 실제로 등록됩니다"
          description="등록된 템플릿은 카카오 검수 대상이 되며, 즉시 취소할 수 없습니다."
        />
        <ul class="confirm-list">
          <li>대상 병원: <strong>{{ companyName }}</strong></li>
          <li v-if="selectedChannel">
            알림톡 채널: <strong>{{ selectedChannel.channelName }}</strong>
            <span class="muted">({{ selectedChannel.senderKey }})</span>
          </li>
          <li>알림톡 템플릿: <strong>{{ parsed ? parsed.stats.templates : 0 }}건</strong> 등록 + 검수 요청</li>
          <li>
            시나리오: <strong>{{ parsed ? parsed.stats.scenarios : 0 }}개</strong>
            <a-tag color="default">draft</a-tag>
            <span class="muted">— 데이터만 밀어 넣습니다. 발송되지 않습니다.</span>
          </li>
          <li>매칭 태그명: <strong>{{ parsed ? parsed.stats.tags : 0 }}개</strong> <span class="muted">(crm 태그는 만들지 않음)</span></li>
        </ul>

        <!-- 이관 = 적재. 켜는 건 검수 통과를 보고 사람이 따로 판단한다. -->
        <a-alert
          type="info"
          show-icon
          class="confirm-note"
          message="시나리오는 활성화되지 않습니다"
          description="이관은 데이터를 밀어 넣기만 합니다. 시나리오는 draft 로 생성되며, 카카오 검수가 승인된 뒤 운영자가 직접 활성화해야 발송이 시작됩니다."
        />

        <div class="modal-footer">
          <a-button @click="confirmVisible = false">취소</a-button>
          <a-button type="primary" class="yellow-button" :loading="applying" @click="handleApply">
            등록 실행
          </a-button>
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { InboxOutlined, FileTextOutlined, ReloadOutlined, RedoOutlined } from "@ant-design/icons-vue";
import ScenarioBlueprint from "./ScenarioBlueprint.vue";
import { BizmsgAuthApi } from "../api";
import {
  readJsonFile,
  parseAfterdocExport,
  automationLinkageStatus,
  extractVariables,
  extractSourceSenderKeys,
  TEMPLATE_KIND_TEXT_MAP,
  TEMPLATE_KIND_COLOR_MAP,
} from "../afterdocParser";
import {
  crmMigrationApi,
  VERDICT_TEXT_MAP,
  VERDICT_COLOR_MAP,
  RESULT_TEXT_MAP,
  RESULT_COLOR_MAP,
  INSPECTION_STATUS_TEXT_MAP,
  INSPECTION_STATUS_COLOR_MAP,
  ITEM_KIND_TEXT_MAP,
} from "../crmMigrationApi";

const PREVIEW_LENGTH = 60;

// 카카오 강조표기 타입 -> 한글. AfterDoc 은 전부 NONE(텍스트)이지만 값이 다를 수도 있어 매핑해둔다.
const EMPHASIZE_TEXT_MAP = {
  NONE: "텍스트",
  TEXT: "강조표기",
  IMAGE: "이미지",
  ITEM_LIST: "아이템리스트",
};

export default {
  name: "AlimtalkMigrationCard",
  components: {
    InboxOutlined,
    FileTextOutlined,
    ReloadOutlined,
    RedoOutlined,
    ScenarioBlueprint,
  },
  props: {
    // 상단 공용 선택기에서 내려주는 대상 병원
    companyId: { type: String, default: undefined },
    companyName: { type: String, default: "-" },
    // 상단에서 한 번 파싱한 data-store.json 객체 (이 카드는 다시 업로드하지 않는다)
    dataStore: { type: Object, default: null },
  },
  setup(props) {
    const currentStep = ref(0);

    // Step 1
    const channelList = ref([]);
    const channelLoading = ref(false);
    const selectedChannelId = ref(undefined);
    const apiBodiesFile = ref(null);
    // 자동화->메시지 연결을 data-store 하나로 풀 수 있는지. needsApiBodies 일 때만 두 번째 파일을 요구한다.
    const linkage = computed(() => automationLinkageStatus(props.dataStore));

    // Step 2
    const parsing = ref(false);
    const parsed = ref(null);

    // Step 3
    const previewing = ref(false);
    const previewResult = ref(null);
    const showProblemOnly = ref(false);
    const selectedBlueprintId = ref(undefined);

    // Step 4
    const confirmVisible = ref(false);
    const applying = ref(false);
    const applyResult = ref(null);
    const inspectionLoading = ref(false);
    const inspectionItems = ref([]);

    const selectedChannel = computed(
      () => channelList.value.find((channel) => channel.channelId === selectedChannelId.value) || null
    );

    // 채널 확정 실패 시 백엔드가 items 의 channel 행에 실제 사유를 담는다
    // (소유 불일치/발신프로필 미발급 등). 상단 알림에 그 사유를 그대로 노출한다.
    const channelErrorReason = computed(() => {
      const items = previewResult.value?.items || [];
      const channelItem = items.find(
        (item) => item.kind === "channel" && item.verdict === "error"
      );
      return channelItem?.reasons?.join(" ") || "";
    });

    // AfterDoc 이 템플릿을 등록할 때 쓴 발신프로필 (파싱 후에만 알 수 있다)
    const afterdocSenderKeys = computed(() =>
      parsed.value ? extractSourceSenderKeys(parsed.value) : []
    );

    const isRecommendedChannel = (channel) =>
      Boolean(channel && channel.senderKey) && afterdocSenderKeys.value.includes(channel.senderKey);

    // 다른 발신프로필을 고르면 템플릿 전부가 신규 등록 + 카카오 재검수 대상이 된다
    const senderKeyMismatch = computed(
      () =>
        Boolean(selectedChannel.value) &&
        afterdocSenderKeys.value.length > 0 &&
        !isRecommendedChannel(selectedChannel.value)
    );

    // 채널까지 확정되고 파싱이 끝나야 다음 단계로 넘어갈 수 있다
    const canGoNext = computed(() => Boolean(selectedChannel.value) && Boolean(parsed.value));

    const truncateSenderKey = (senderKey) => {
      const value = String(senderKey || "");
      return value.length > 18 ? `${value.slice(0, 18)}...` : value;
    };

    // 서버 응답 메시지를 우선 노출 (원인 파악 가능하도록)
    const resolveErrorMessage = (error, fallback) => {
      const data = error && error.response ? error.response.data : null;
      if (data && data.message) {
        return Array.isArray(data.message) ? data.message.join(", ") : data.message;
      }
      return fallback;
    };

    // 병원의 알림톡 채널(발신프로필) 목록 조회
    const loadChannels = async (companyId) => {
      channelList.value = [];
      selectedChannelId.value = undefined;
      if (!companyId) return;

      channelLoading.value = true;
      try {
        const auth = await BizmsgAuthApi.getBizmsgAuthByCompanyId(companyId);
        channelList.value = (auth && auth.channels) || [];
        // 채널이 하나뿐이면 자동 선택하되, 화면에는 그대로 노출해 대상이 숨겨지지 않게 한다
        if (channelList.value.length === 1) {
          selectedChannelId.value = channelList.value[0].channelId;
        }
      } catch (error) {
        // 인증정보 문서 자체가 없는 병원은 404 -> "채널 없음" 안내로 충분하다
        const status = error && error.response ? error.response.status : 0;
        if (status !== 404) {
          console.error("채널 목록 조회 실패:", error);
          message.error(resolveErrorMessage(error, "채널 목록을 불러오지 못했습니다."));
        }
      } finally {
        channelLoading.value = false;
      }
    };

    // 병원이 바뀌면 채널과 이전 결과를 모두 초기화한다
    watch(
      () => props.companyId,
      (companyId) => {
        previewResult.value = null;
        applyResult.value = null;
        inspectionItems.value = [];
        currentStep.value = 0;
        loadChannels(companyId);
      },
      { immediate: true }
    );

    const handleApiBodiesSelect = (file) => {
      if (!file.name.match(/\.json$/i)) {
        message.warning("JSON 파일만 업로드 가능합니다.");
        return false;
      }
      apiBodiesFile.value = file;
      return false; // 브라우저 파싱만 — 서버 업로드 막는다
    };

    // 브라우저에서만 파싱 (네트워크 호출 없음). data-store 는 상단에서 내려온 객체를 쓴다.
    // apiBodies 는 연결 파일이 필요한 경우(needsApiBodies)에만 넘어온다.
    const runParse = async (apiBodies) => {
      if (!props.dataStore) return;

      parsing.value = true;
      try {
        const result = parseAfterdocExport(props.dataStore, apiBodies || []);
        if (result.stats.templates === 0 && result.stats.scenarios === 0 && result.stats.tags === 0) {
          message.warning("추출된 알림톡 데이터가 없습니다. 파일을 확인해주세요.");
          return;
        }

        parsed.value = result;
        message.success(
          `템플릿 ${result.stats.templates}건, 시나리오 ${result.stats.scenarios}개, 태그 ${result.stats.tags}개를 추출했습니다.`
        );
      } catch (error) {
        console.error("파싱 실패:", error);
        message.error(error.message || "파일 파싱 중 오류가 발생했습니다.");
      } finally {
        parsing.value = false;
      }
    };

    // 연결 파일(collect-api-bodies.json)을 읽어 파싱한다. needsApiBodies 인 경우에만 쓴다.
    const runParseWithApiBodies = async () => {
      if (!apiBodiesFile.value) return;
      let apiBodies;
      try {
        apiBodies = await readJsonFile(apiBodiesFile.value);
      } catch (error) {
        message.error(error.message || "파일을 읽지 못했습니다.");
        return;
      }
      if (!Array.isArray(apiBodies)) {
        message.warning("collect-api-bodies.json 은 배열 형식이어야 합니다.");
        return;
      }
      await runParse(apiBodies);
    };

    // data-store 만으로 연결이 풀리면(automationId 존재 또는 자동화 없음) 파일 없이 즉시 파싱한다.
    const maybeParseFromStore = () => {
      if (props.dataStore && !linkage.value.needsApiBodies) runParse([]);
    };

    // 연결 파일이 올라오면 즉시 파싱한다 (파일을 바꾸면 이전 결과는 버린다)
    watch(apiBodiesFile, () => {
      parsed.value = null;
      previewResult.value = null;
      applyResult.value = null;
      if (apiBodiesFile.value) runParseWithApiBodies();
    });

    // 상단에서 data-store 가 바뀌면: store 로 풀리면 파일 없이 즉시, 아니면 이미 올린 연결 파일로 재파싱한다.
    watch(
      () => props.dataStore,
      () => {
        parsed.value = null;
        previewResult.value = null;
        applyResult.value = null;
        if (!props.dataStore) return;
        if (linkage.value.needsApiBodies) {
          if (apiBodiesFile.value) runParseWithApiBodies();
        } else {
          maybeParseFromStore();
        }
      },
      { immediate: true }
    );

    // 병원/채널을 지운 채 뒤 단계로 되돌아온 경우를 막는다
    const ensureTarget = () => {
      if (parsed.value && selectedChannel.value && props.companyId) return true;
      message.warning("대상 병원과 알림톡 채널을 먼저 선택해주세요.");
      confirmVisible.value = false;
      currentStep.value = 0;
      return false;
    };

    // 정규화된 payload 구성. 재시도도 항상 전체를 보낸다.
    // apply 는 멱등이라 이미 만들어진 템플릿/시나리오는 서버가 skip 한다.
    const buildPayload = () => {
      const source = parsed.value;
      const channel = selectedChannel.value;
      return {
        channelId: channel.channelId,
        senderKey: channel.senderKey,
        tags: source.tags,
        templates: source.templates,
        scenarios: source.scenarios,
      };
    };

    const handlePreview = async () => {
      if (!ensureTarget()) return;

      previewing.value = true;
      try {
        const response = await crmMigrationApi.preview(props.companyId, buildPayload());
        previewResult.value = response.data;
        currentStep.value = 2;
      } catch (error) {
        console.error("드라이런 실패:", error);
        message.error(resolveErrorMessage(error, "드라이런 중 오류가 발생했습니다."));
      } finally {
        previewing.value = false;
      }
    };

    const runApply = async () => {
      if (!ensureTarget()) return;

      applying.value = true;
      try {
        const response = await crmMigrationApi.apply(props.companyId, buildPayload());
        applyResult.value = response.data;
        confirmVisible.value = false;
        currentStep.value = 3;
        await handleRefreshInspection();

        const failedCount = (response.data.items || []).filter((item) => item.result === "failed").length;
        if (failedCount > 0) {
          message.warning(`${failedCount}건이 실패했습니다. 결과 표를 확인해주세요.`);
        } else {
          message.success("등록이 완료되었습니다.");
        }
      } catch (error) {
        console.error("등록 실행 실패:", error);
        message.error(resolveErrorMessage(error, "등록 실행 중 오류가 발생했습니다."));
      } finally {
        applying.value = false;
      }
    };

    const handleApply = () => runApply();

    // 전체를 다시 보내되, 성공분은 서버가 skip 하므로 실질적으로 실패건만 재시도된다.
    const handleRetryFailed = () => runApply();

    const handleRefreshInspection = async () => {
      inspectionLoading.value = true;
      try {
        // 등록에 쓴 채널과 같은 채널에서 검수 상태를 읽어야 한다
        const response = await crmMigrationApi.getInspectionStatus(
          props.companyId,
          selectedChannelId.value
        );
        inspectionItems.value = (response.data && response.data.items) || [];
      } catch (error) {
        console.error("검수 상태 조회 실패:", error);
        message.error(resolveErrorMessage(error, "검수 상태를 불러오지 못했습니다."));
      } finally {
        inspectionLoading.value = false;
      }
    };

    const failedItems = computed(() => {
      if (!applyResult.value) return [];
      return (applyResult.value.items || []).filter((item) => item.result === "failed");
    });

    const previewItems = computed(() => {
      if (!previewResult.value) return [];
      const items = previewResult.value.items || [];
      if (!showProblemOnly.value) return items;
      return items.filter((item) => item.verdict === "warn" || item.verdict === "error");
    });

    // 실제로 저장될 플로우. 백엔드가 등록 코드와 같은 경로로 조립해 내려준다.
    const blueprints = computed(() => (previewResult.value && previewResult.value.blueprints) || []);

    const blockedBlueprintCount = computed(
      () => blueprints.value.filter((blueprint) => blueprint.blockers.length > 0).length
    );

    const selectedBlueprint = computed(
      () => blueprints.value.find((blueprint) => blueprint.sourceId === selectedBlueprintId.value) || null
    );

    // 드라이런을 다시 돌리면 이전 선택이 사라질 수 있으므로 첫 시나리오로 되돌린다
    watch(blueprints, (list) => {
      const stillThere = list.some((blueprint) => blueprint.sourceId === selectedBlueprintId.value);
      if (!stillThere) selectedBlueprintId.value = list.length ? list[0].sourceId : undefined;
    });

    const tagNameMap = computed(() => {
      const map = new Map();
      if (parsed.value) {
        for (const tag of parsed.value.tags) {
          map.set(tag.sourceId, tag.name);
        }
      }
      return map;
    });

    const templateRows = computed(() => {
      if (!parsed.value) return [];
      const channelSenderKey = selectedChannel.value ? selectedChannel.value.senderKey : null;
      return parsed.value.templates.map((template) => {
        // AfterDoc 은 전부 텍스트형이지만 원본 값이 있으면 그대로 쓰고, 없으면 BA/NONE 으로 본다
        const messageType = template.templateMessageType || "BA";
        const emphasizeType = template.templateEmphasizeType || "NONE";
        // 주의사항 템플릿은 AfterDoc 발신프로필에 이미 승인돼 있어, 같은 발신프로필이면 재등록 없이 채택된다
        const adopted = Boolean(
          template.kind === "caution" &&
            template.sourceSenderKey &&
            channelSenderKey &&
            template.sourceSenderKey === channelSenderKey
        );
        return {
          ...template,
          messageType,
          emphasizeType,
          emphasizeLabel: EMPHASIZE_TEXT_MAP[emphasizeType] || emphasizeType,
          adopted,
          preview:
            template.content.length > PREVIEW_LENGTH
              ? `${template.content.slice(0, PREVIEW_LENGTH)}...`
              : template.content,
          variables: extractVariables(template.content),
          charCount: template.content.length,
        };
      });
    });

    const scenarioRows = computed(() => {
      if (!parsed.value) return [];
      return parsed.value.scenarios.map((scenario) => ({
        ...scenario,
        tagNames: scenario.tagSourceIds.map((tagId) => tagNameMap.value.get(tagId) || tagId),
        messageCount: scenario.messages.length,
        timeline: scenario.messages
          .map((msg) => `D+${msg.daysAfter ?? 0} ${msg.timeOfDay || ""}`.trim())
          .join(" · "),
      }));
    });

    const templateColumns = [
      { title: "템플릿명", dataIndex: "name", key: "name", width: 200, ellipsis: true },
      { title: "종류", key: "kind", width: 90, align: "center" },
      { title: "카카오 타입", key: "type", width: 180 },
      { title: "검수", key: "inspection", width: 120, align: "center" },
      { title: "내용 미리보기", key: "preview", width: 300 },
      { title: "변수", key: "variables", width: 200 },
      { title: "글자수", dataIndex: "charCount", key: "charCount", width: 80, align: "right" },
    ];

    const scenarioColumns = [
      { title: "시나리오명", dataIndex: "name", key: "name", width: 200, ellipsis: true },
      { title: "활성", key: "isActive", width: 80, align: "center" },
      { title: "연결 태그", key: "tagNames", width: 240 },
      { title: "메시지", dataIndex: "messageCount", key: "messageCount", width: 80, align: "right" },
      { title: "발송 타임라인", key: "timeline", width: 400 },
    ];

    const tagColumns = [
      { title: "태그명", dataIndex: "name", key: "name" },
      { title: "색상", key: "color", width: 200 },
    ];

    const previewColumns = [
      { title: "종류", key: "kind", width: 100, align: "center" },
      { title: "이름", dataIndex: "name", key: "name", width: 240, ellipsis: true },
      { title: "판정", key: "verdict", width: 90, align: "center" },
      { title: "사유", key: "reasons" },
    ];

    const applyColumns = [
      { title: "종류", key: "kind", width: 100, align: "center" },
      { title: "이름", dataIndex: "name", key: "name", width: 240, ellipsis: true },
      { title: "결과", key: "result", width: 150, align: "center" },
      { title: "템플릿 코드", dataIndex: "templateCode", key: "templateCode", width: 180 },
      { title: "오류", key: "error" },
    ];

    const inspectionColumns = [
      { title: "템플릿 코드", dataIndex: "templateCode", key: "templateCode", width: 200 },
      { title: "템플릿명", dataIndex: "templateName", key: "templateName", width: 240, ellipsis: true },
      { title: "검수 상태", key: "status", width: 100, align: "center" },
      { title: "코멘트", key: "comment" },
    ];

    return {
      currentStep,
      channelList,
      channelLoading,
      selectedChannelId,
      selectedChannel,
      afterdocSenderKeys,
      isRecommendedChannel,
      senderKeyMismatch,
      truncateSenderKey,
      apiBodiesFile,
      handleApiBodiesSelect,
      linkage,
      canGoNext,
      parsing,
      parsed,
      templateRows,
      scenarioRows,
      previewing,
      previewResult,
      channelErrorReason,
      previewItems,
      showProblemOnly,
      blueprints,
      blockedBlueprintCount,
      selectedBlueprintId,
      selectedBlueprint,
      handlePreview,
      confirmVisible,
      applying,
      applyResult,
      failedItems,
      handleApply,
      handleRetryFailed,
      inspectionLoading,
      inspectionItems,
      handleRefreshInspection,
      templateColumns,
      scenarioColumns,
      tagColumns,
      previewColumns,
      applyColumns,
      inspectionColumns,
      TEMPLATE_KIND_TEXT_MAP,
      TEMPLATE_KIND_COLOR_MAP,
      VERDICT_TEXT_MAP,
      VERDICT_COLOR_MAP,
      RESULT_TEXT_MAP,
      RESULT_COLOR_MAP,
      INSPECTION_STATUS_TEXT_MAP,
      INSPECTION_STATUS_COLOR_MAP,
      ITEM_KIND_TEXT_MAP,
    };
  },
};
</script>

<style scoped>
.migration-steps {
  margin-bottom: 28px;
}

.step-body {
  padding-top: 8px;
}

.need-company {
  margin-bottom: 24px;
}

.field-block {
  margin-bottom: 24px;
}

.field-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
}

.required-mark {
  color: #f5222d;
}

.field-hint {
  margin: 8px 0 0;
  font-size: 12px;
  color: #999;
}

.channel-summary {
  margin-top: 12px;
  padding: 10px 16px;
  background: #fafafa;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  max-width: 520px;
}

.channel-summary-row {
  display: flex;
  align-items: center;
  gap: 8px;
  line-height: 2;
  flex-wrap: wrap;
}

.channel-summary-row .sender-key {
  font-family: monospace;
  font-size: 12px;
  color: #333;
  word-break: break-all;
}

.sender-key-inline {
  font-family: monospace;
  font-size: 12px;
}

.type-notice,
.tag-notice {
  margin-bottom: 12px;
}

.type-chip {
  font-family: monospace;
}

.channel-warning {
  margin-top: 12px;
  max-width: 720px;
}

.selected-file {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 6px;
  margin-top: 12px;
}

.selected-file .file-name {
  flex: 1;
  font-weight: 500;
  color: #333;
}

.stats-cards {
  margin-bottom: 20px;
}

.stats-cards .ant-card {
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

:deep(.ant-statistic-title) {
  font-size: 14px;
  color: #666;
}

:deep(.ant-statistic-content) {
  font-size: 24px;
  font-weight: 600;
}

.summary-strip {
  margin-bottom: 16px;
}

.filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.blueprint-section {
  margin-bottom: 24px;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fff;
}

.section-header {
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.section-header h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.blueprint-layout {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.blueprint-picker {
  flex: 0 0 260px;
  max-height: 560px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
}

.picker-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  font-size: 13px;
}

.picker-item:hover {
  background: #fafafa;
}

.picker-item.active {
  background: #fffbe6;
  border-left: 3px solid #ffcc32;
  padding-left: 9px;
}

.picker-dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #d9d9d9;
}

.picker-dot.blocked {
  background: #f5222d;
}

.picker-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #333;
}

.picker-count {
  flex: 0 0 auto;
  font-size: 12px;
}

/* 긴 JSON / 칩이 페이지를 가로로 밀지 않도록 이 안에서만 스크롤시킨다 */
.blueprint-detail {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
}

.block-alert {
  margin-bottom: 16px;
}

.content-preview {
  color: #555;
  white-space: pre-wrap;
  word-break: break-all;
}

.timeline {
  color: #555;
  font-size: 12px;
}

.color-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.color-swatch {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
  display: inline-block;
}

.reason-list {
  margin: 0;
  padding-left: 16px;
}

.error-text {
  color: #f5222d;
}

.muted {
  color: #999;
}

.inspection-note {
  margin-bottom: 12px;
}

.inspection-panel {
  margin-top: 32px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.inspection-panel .panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.inspection-panel h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.step-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.confirm-note {
  margin-top: 12px;
}

.confirm-list {
  margin: 16px 0 0;
  padding-left: 18px;
  line-height: 1.9;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
  margin-top: 20px;
}

.yellow-button {
  background-color: #ffcc32;
  border-color: #ffcc32;
  color: #333;
}

.yellow-button:hover {
  background-color: #e6b82e;
  border-color: #e6b82e;
  color: #333;
}
</style>
